/**
 * Retail Operations Overview (Sprint 1, Oct 2026).
 *
 * Shown instead of the lead / conversation Overview when the SERVER says
 * this tenant's `dashboard_experience` is `retail_operations` (see
 * `backend/services/dashboard_experience.py`). Approved design:
 * `design-preview/retail-dashboard/` at commit 32d3c1d.
 *
 * One request (`GET /api/retail/reports/overview`) feeds the whole page.
 * Changing the period or catalog refetches immediately — the Overview has
 * no Generate button. All money is integer cents from the server, all date
 * windows are in the tenant's own timezone.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Info } from "lucide-react";
import api from "../lib/api";
import {
  PERIODS, change, count, familyColor, money, money0, rangeLabel, reportError, share,
} from "../lib/retailReporting";
import SalesTrendChart from "../components/retail-reporting/SalesTrendChart";
import FamilyBreakdown from "../components/retail-reporting/FamilyBreakdown";

const NAVY_SOFT = "#A9B8D3";
const NAVY = "#264073";

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function daysBefore(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d - n));
  return t.toISOString().slice(0, 10);
}

function Delta({ current, previous }) {
  const c = change(current, previous);
  if (c === null) return null;
  const flat = Math.abs(c) < 0.005;
  const cls = flat ? "bg-secondary text-muted-foreground" : c > 0 ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800";
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground" data-testid="kpi-delta">
      <span className={`inline-flex items-center rounded-full px-1.5 py-px font-semibold tabular-nums ${cls}`}>
        {flat ? "→" : c > 0 ? "↑" : "↓"} {Math.abs(c * 100).toFixed(1)}%
      </span>
      vs previous
    </div>
  );
}

function Kpi({ label, value, current, previous, help, note, testid }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border bg-card px-4 py-3.5 shadow-sm" data-testid={testid}>
      <div className="flex min-h-[2.6em] items-start gap-1.5 text-xs font-medium uppercase leading-snug tracking-[0.05em] text-muted-foreground">
        <span>{label}</span>
        <span title={help} aria-label={`About ${label}: ${help}`} className="mt-px text-muted-foreground/70"><Info className="h-3.5 w-3.5" /></span>
      </div>
      <div className="whitespace-nowrap font-display text-[28px] font-semibold leading-tight tabular-nums">{value}</div>
      <Delta current={current} previous={previous} />
      {note}
    </div>
  );
}

function ShareCard({ title, rows, total, foot, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.net_sales_cents));
  return (
    <section className="rounded-xl border bg-card shadow-sm min-w-0" data-testid={testid}>
      <div className="px-[18px] pt-4">
        <h2 className="font-display text-[17px] font-semibold">{title}</h2>
        <p className="text-[12.5px] text-muted-foreground">Net Sales · orders · share of Net Sales</p>
      </div>
      <div className="px-[18px] pb-4 pt-3">
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">No sales in this period</p> : (
          <ul className="flex flex-col gap-2.5">
            {rows.map((r) => (
              <li key={r.key} className="flex flex-wrap items-baseline justify-between gap-x-2.5 gap-y-1 text-[13px]" data-testid={`${testid}-row`}>
                <span className="min-w-0 flex-1 truncate">{r.label}</span>
                <span className="flex-none whitespace-nowrap tabular-nums">
                  {money0(r.net_sales_cents)}
                  <span className="text-muted-foreground"> · {count(r.orders)} {r.orders === 1 ? "order" : "orders"} · </span>
                  <b className="font-semibold">{share(r.net_sales_cents, total)}</b>
                </span>
                <span className="relative h-1.5 basis-full" aria-hidden="true">
                  <span className="absolute inset-y-0 left-0 rounded-r" style={{ width: `${Math.max(0.5, (r.net_sales_cents / max) * 100)}%`, background: NAVY }} />
                </span>
              </li>
            ))}
          </ul>
        )}
        {foot && rows.length > 0 && <p className="mt-2.5 text-xs text-muted-foreground">{foot}</p>}
      </div>
    </section>
  );
}

export function RetailOverviewContent() {
  const [period, setPeriod] = useState("last30");
  const [custom, setCustom] = useState(() => ({ start: daysBefore(todayIso(), 13), end: todayIso() }));
  const [catalogId, setCatalogId] = useState("");
  const [metric, setMetric] = useState("net");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const params = { period };
    if (period === "custom") Object.assign(params, custom);
    if (catalogId) params.catalog_id = catalogId;
    setLoading(true);
    setError("");
    return api.get("/retail/reports/overview", { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(reportError(e)))
      .finally(() => setLoading(false));
  }, [period, custom, catalogId]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpis;
  const pk = data?.comparison_kpis;
  const p = data?.period;
  const catalogs = data?.catalogs || [];
  const net = k?.net_sales_cents || 0;
  const throughHour = p?.key === "today" && p?.through_local_time ? Number(p.through_local_time.split(":")[0]) : null;
  const catalogSales = data?.catalog_sales || [];
  const showCatalogStrip = !catalogId && catalogSales.length > 1;
  const breakdownKey = useMemo(() => `${period}|${custom.start}|${custom.end}|${catalogId}`, [period, custom, catalogId]);
  const selectedCatalog = catalogs.find((c) => c.catalog_id === catalogId);

  return (
    <div className="flex max-w-[1480px] flex-col gap-[18px]" data-testid="retail-overview">
      <header className="flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-semibold" data-testid="retail-overview-title">Overview</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Sales from paid orders · Times in {p?.timezone ? p.timezone.replace(/_/g, " ") : "your business time zone"}
          </p>
        </div>
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2.5">
          {catalogs.length > 1 && (
            <label className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
              Catalog
              <select value={catalogId} onChange={(e) => setCatalogId(e.target.value)} data-testid="overview-catalog"
                className="max-w-[220px] rounded-[10px] border bg-card px-3 py-2 text-[13.5px] text-foreground shadow-sm">
                <option value="">All catalogs</option>
                {catalogs.map((c) => <option key={c.catalog_id} value={c.catalog_id}>{c.name || c.catalog_id}</option>)}
              </select>
            </label>
          )}
          <div role="group" aria-label="Period" className="inline-flex max-w-full overflow-x-auto rounded-[10px] border bg-card p-[3px] shadow-sm">
            {PERIODS.map((x) => (
              <button key={x.key} type="button" aria-pressed={period === x.key} onClick={() => setPeriod(x.key)}
                data-testid={`period-${x.key}`}
                className={`whitespace-nowrap rounded-[7px] px-3 py-[7px] text-[13.5px] ${period === x.key ? "bg-primary font-medium text-primary-foreground" : "text-foreground/80 hover:bg-secondary"}`}>
                {x.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {period === "custom" && (
        <div role="group" aria-label="Custom date range" className="flex flex-wrap items-center gap-2 rounded-[10px] border bg-card px-3 py-2 text-[13px]">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <label htmlFor="custom-start">From</label>
          <input id="custom-start" type="date" value={custom.start} max={custom.end}
            onChange={(e) => e.target.value && setCustom((c) => ({ ...c, start: e.target.value }))}
            className="rounded-lg border bg-card px-2 py-1" />
          <label htmlFor="custom-end">to</label>
          <input id="custom-end" type="date" value={custom.end} min={custom.start} max={todayIso()}
            onChange={(e) => e.target.value && setCustom((c) => ({ ...c, end: e.target.value }))}
            className="rounded-lg border bg-card px-2 py-1" />
          <span className="text-muted-foreground">Up to 366 days. Weeks start on Monday.</span>
        </div>
      )}

      {p && (
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12.5px] text-muted-foreground" data-testid="overview-range">
          <Calendar className="h-4 w-4" />
          <b className="font-medium text-foreground/80">{rangeLabel(p.start_date, p.end_date)}{p.key === "today" && p.through_local_time ? `, through ${p.through_local_time}` : ""}</b>
          {p.comparison && <span>Compared with {p.comparison.label.toLowerCase()} ({rangeLabel(p.comparison.start_date, p.comparison.end_date)})</span>}
          {selectedCatalog && <span className="rounded-full border bg-card px-2.5 py-0.5">Catalog: <b className="text-foreground">{selectedCatalog.name}</b> {selectedCatalog.display_code && <span>{selectedCatalog.display_code}</span>}</span>}
        </div>
      )}

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900" data-testid="overview-error">
          <Info className="mt-0.5 h-4 w-4 flex-none" /><span>{error}</span>
        </div>
      )}

      {!data && loading && <div className="h-40 animate-pulse rounded-xl bg-secondary/60" aria-label="Loading sales" />}

      {k && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" style={{ opacity: loading ? 0.6 : 1 }}>
            <Kpi testid="kpi-net-sales" label="Net Sales" value={money(k.net_sales_cents)} current={k.net_sales_cents} previous={pk?.net_sales_cents}
              help="Sales of items after discounts and refunds. Excludes tax, tips, delivery and shipping charges." />
            <Kpi testid="kpi-orders" label="Orders" value={count(k.orders)} current={k.orders} previous={pk?.orders}
              help="Paid orders placed in this period. Drafts, test orders, unpaid and cancelled orders are not counted." />
            <Kpi testid="kpi-aov" label="Average Order Value" value={money(k.average_order_value_cents)} current={k.average_order_value_cents} previous={pk?.average_order_value_cents}
              help="Net Sales divided by Orders." />
            <Kpi testid="kpi-customers" label="Customers" value={count(k.customers)} current={k.customers} previous={pk?.customers}
              help="Identified customers who placed a paid order. Guest orders have no email or phone, so they are not counted as customers."
              note={<div className="text-xs text-muted-foreground" data-testid="guest-orders">{count(k.guest_orders)} guest {k.guest_orders === 1 ? "order" : "orders"}</div>} />
            <Kpi testid="kpi-new-customers" label="New Customers" value={count(k.new_customers)} current={k.new_customers} previous={pk?.new_customers}
              help="Customers whose first ever paid order was in this period." />
            <Kpi testid="kpi-repeat-customers" label="Repeat Customers" value={count(k.repeat_customers)} current={k.repeat_customers} previous={pk?.repeat_customers}
              help="Customers in this period who had a paid order before it." />
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
            <section className="rounded-xl border bg-card shadow-sm min-w-0" aria-labelledby="trend-title">
              <div className="flex items-start justify-between gap-3 px-[18px] pt-4">
                <div>
                  <h2 id="trend-title" className="font-display text-[17px] font-semibold">Sales trend</h2>
                  <p className="text-[12.5px] text-muted-foreground">{data.trend.granularity === "hour" ? "By hour" : "By day"}</p>
                </div>
                <div role="group" aria-label="Measure" className="inline-flex flex-none rounded-lg border bg-secondary/60 p-0.5">
                  {[["net", "Net Sales"], ["orders", "Orders"]].map(([key, l]) => (
                    <button key={key} type="button" aria-pressed={metric === key} onClick={() => setMetric(key)}
                      className={`rounded-md px-2.5 py-1 text-[12.5px] ${metric === key ? "bg-card font-medium shadow-sm" : "text-muted-foreground"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="px-[18px] pb-4 pt-3">
                {k.orders === 0 ? <p className="py-16 text-center text-sm text-muted-foreground" data-testid="trend-empty">No sales in the selected period</p>
                  : <SalesTrendChart trend={data.trend} metric={metric} throughHour={throughHour} />}
              </div>
              {showCatalogStrip && (
                <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t px-[18px] py-2.5 text-[12.5px] text-muted-foreground" data-testid="catalog-sales">
                  <span>Catalog sales</span>
                  {catalogSales.map((c) => (
                    <button key={c.catalog_id || "none"} type="button" disabled={!c.catalog_id}
                      title={c.display_code ? `${c.label} · ${c.display_code}` : c.label}
                      onClick={() => c.catalog_id && setCatalogId(c.catalog_id)}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border bg-card px-2.5 py-0.5 text-foreground/80 hover:border-foreground/40">
                      {c.label} <b className="font-semibold tabular-nums text-foreground">{money0(c.net_sales_cents)}</b>
                      <span className="text-muted-foreground">{share(c.net_sales_cents, net)}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
            <FamilyBreakdown key={breakdownKey} breakdown={data.breakdown} />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <section className="rounded-xl border bg-card shadow-sm min-w-0" data-testid="top-products">
              <div className="px-[18px] pt-4">
                <h2 className="font-display text-[17px] font-semibold">Top products</h2>
                <p className="text-[12.5px] text-muted-foreground">By Net Sales</p>
              </div>
              <div className="px-[18px] pb-4 pt-3">
                {data.top_products.length === 0 ? <p className="text-sm text-muted-foreground">No sales in this period</p> : (
                  <ol className="flex flex-col gap-2.5">
                    {data.top_products.map((t, i) => (
                      <li key={t.product_id || t.name} className="flex items-baseline gap-2 text-[13px]">
                        <span className="w-[18px] flex-none text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                        <i className="h-2.5 w-2.5 flex-none self-center rounded-[3px]" aria-hidden="true"
                          style={{ background: familyColor(t.family_key ? t.family_label : "") }} />
                        <span className="min-w-0 flex-1 truncate" title={t.name}>{t.name}</span>
                        <span className="flex-none whitespace-nowrap tabular-nums">{money0(t.net_sales_cents)}<span className="ml-1.5 text-muted-foreground">{count(t.units)} units</span></span>
                      </li>
                    ))}
                  </ol>
                )}
                {data.breakdown.mode === "family" && data.top_products.length > 0 && <p className="mt-2.5 text-xs text-muted-foreground">Dot shows the product's family.</p>}
              </div>
            </section>
            <ShareCard testid="fulfillment-card" title="Fulfillment method" rows={data.fulfillment} total={net}
              foot={data.scheduled_orders ? `${count(data.scheduled_orders)} scheduled ${data.scheduled_orders === 1 ? "order is" : "orders are"} included under their method.` : null} />
            <ShareCard testid="payment-card" title="Payment method" rows={data.payment} total={net}
              foot="Card includes online and in-store card payments." />
            <section className="rounded-xl border bg-card shadow-sm min-w-0" data-testid="customers-card">
              <div className="px-[18px] pt-4">
                <h2 className="font-display text-[17px] font-semibold">Customers</h2>
                <p className="text-[12.5px] text-muted-foreground">New vs returning</p>
              </div>
              <div className="px-[18px] pb-4 pt-3">
                {k.customers === 0 ? <p className="text-sm text-muted-foreground" data-testid="no-customers">No identified customers in the selected period</p> : (
                  <>
                    <div className="my-2 flex h-3 gap-0.5" role="img" aria-label={`${k.new_customers} new and ${k.repeat_customers} returning customers`}>
                      {k.new_customers > 0 && <i className="block h-full rounded-l" style={{ width: `${(k.new_customers / k.customers) * 100}%`, background: NAVY_SOFT }} />}
                      {k.repeat_customers > 0 && <i className="block h-full rounded-r" style={{ width: `${(k.repeat_customers / k.customers) * 100}%`, background: NAVY }} />}
                    </div>
                    <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-[12.5px]">
                      <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-[3px]" style={{ background: NAVY_SOFT }} />New <b className="tabular-nums">{count(k.new_customers)}</b></span>
                      <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-[3px]" style={{ background: NAVY }} />Returning <b className="tabular-nums">{count(k.repeat_customers)}</b></span>
                    </div>
                  </>
                )}
                {data.membership?.enabled && (
                  <div className="mt-3.5 border-t pt-3.5" data-testid="member-sales">
                    <div className="text-[11.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">Member sales</div>
                    {data.membership.member_orders > 0 ? (
                      <>
                        <div className="flex flex-wrap items-baseline gap-2.5">
                          <span className="font-display text-[26px] font-semibold tabular-nums">{share(data.membership.member_net_sales_cents, net)}</span>
                          <span className="text-[13px] text-muted-foreground">of Net Sales</span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {count(data.membership.member_orders)} member {data.membership.member_orders === 1 ? "order" : "orders"} · {money0(data.membership.member_net_sales_cents)}. "Member" means an active member when the order was placed.
                        </p>
                      </>
                    ) : <p className="mt-1 text-sm text-muted-foreground">No member sales data in this period</p>}
                  </div>
                )}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

export default RetailOverviewContent;
